import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import ConfirmBar from './ConfirmBar';
import ResaleFinalizedView from './ResaleFinalizedView';
import { resolveMediaObject, kindFromName } from '../lib/bookingMedia';
import { displayName } from '../lib/format';
import {
  RESALE_SECTION_KEYS,
  RESALE_SECTION_LABELS,
  RESALE_STATUS_OPTIONS,
  computeResaleScores,
  emptyResaleSections,
  normalizeResaleSections,
  suggestedScoreForStatus,
} from '../lib/resaleScore';
import { supabase } from '../lib/supabase';

function emptyRepair() {
  return {
    key: crypto.randomUUID(),
    title: '',
    priority: 'soon',
    cost_min_egp: '',
    cost_max_egp: '',
    notes: '',
  };
}

function emptyMaintenance() {
  return {
    key: crypto.randomUUID(),
    item: '',
    horizon_months: '',
    cost_egp: '',
    notes: '',
  };
}

function formFromReport(report, booking) {
  const vehicle = booking?.vehicles || {};
  const profile = booking?.profiles || {};
  return {
    sellerName: report?.seller_name || displayName(profile) || '',
    sellerPhone: report?.seller_phone || profile?.phone || '',
    sellerCity: report?.seller_city || '',
    brand: report?.brand || vehicle.brand || '',
    model: report?.model || vehicle.model || '',
    year: report?.year ? String(report.year) : vehicle.year ? String(vehicle.year) : '',
    plateNumber: report?.plate_number || vehicle.plate_number || '',
    vin: report?.vin || vehicle.vin || '',
    mileageKm:
      report?.mileage_km > 0
        ? String(report.mileage_km)
        : vehicle.mileage
          ? String(vehicle.mileage)
          : '',
    color: report?.color || '',
    fuelType: report?.fuel_type || '',
    transmission: report?.transmission || vehicle.transmission || '',
    engineSize: report?.engine_size || vehicle.engine || '',
    sections: normalizeResaleSections(report?.sections || emptyResaleSections()),
    immediateRepairs: (report?.immediate_repairs || []).length
      ? report.immediate_repairs.map((row, index) => ({
          key: `${index}`,
          title: row.title || '',
          priority: row.priority || 'soon',
          cost_min_egp: row.cost_min_egp ?? '',
          cost_max_egp: row.cost_max_egp ?? '',
          notes: row.notes || '',
        }))
      : [emptyRepair()],
    expectedMaintenance: (report?.expected_maintenance || []).length
      ? report.expected_maintenance.map((row, index) => ({
          key: `${index}`,
          item: row.item || '',
          horizon_months: row.horizon_months ?? '',
          cost_egp: row.cost_egp ?? '',
          notes: row.notes || '',
        }))
      : [emptyMaintenance()],
    summaryNotes: report?.summary_notes || '',
    inspectorName: report?.inspector_name || '',
    media: Array.isArray(report?.inspection_media) ? report.inspection_media : [],
  };
}

function reportPayload(form, scores) {
  return {
    seller_name: form.sellerName.trim(),
    seller_phone: form.sellerPhone.trim(),
    seller_city: form.sellerCity.trim() || null,
    brand: form.brand.trim() || null,
    model: form.model.trim() || null,
    year: form.year ? Number(form.year) : null,
    plate_number: form.plateNumber.trim() || null,
    vin: form.vin.trim() || null,
    mileage_km: Number(form.mileageKm) || 0,
    color: form.color.trim() || null,
    fuel_type: form.fuelType.trim() || null,
    transmission: form.transmission.trim() || null,
    engine_size: form.engineSize.trim() || null,
    sections: form.sections,
    overall_score: scores.overallScore,
    market_condition: scores.marketCondition,
    immediate_repairs: form.immediateRepairs
      .filter((row) => row.title.trim())
      .map((row) => ({
        title: row.title.trim(),
        priority: row.priority || 'soon',
        cost_min_egp: row.cost_min_egp === '' ? null : Number(row.cost_min_egp),
        cost_max_egp: row.cost_max_egp === '' ? null : Number(row.cost_max_egp),
        notes: row.notes?.trim() || null,
      })),
    expected_maintenance: form.expectedMaintenance
      .filter((row) => row.item.trim())
      .map((row) => ({
        item: row.item.trim(),
        horizon_months:
          row.horizon_months === '' ? null : Number(row.horizon_months),
        cost_egp: row.cost_egp === '' ? null : Number(row.cost_egp),
        notes: row.notes?.trim() || null,
      })),
    summary_notes: form.summaryNotes.trim() || null,
    inspector_name: form.inspectorName.trim() || null,
    inspection_media: form.media,
  };
}

function ResaleMediaThumb({ item }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    resolveMediaObject(item).then((resolved) => {
      if (alive) setUrl(resolved.url);
    });
    return () => {
      alive = false;
    };
  }, [item]);
  if (!url) return <div className="resale-media-thumb loading">…</div>;
  if (item.kind === 'video') {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="resale-media-thumb video">
        Video
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="resale-media-thumb">
      <img src={url} alt="" />
    </a>
  );
}

export default forwardRef(function ResaleInspectionPanel({
  reportId,
  booking,
  branchId,
  panelMode,
  onFinalized,
  onReportChange,
  onDeleted,
}, ref) {
  const [report, setReport] = useState(null);
  const [form, setForm] = useState(() => formFromReport(null, booking));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const scores = useMemo(() => computeResaleScores(form.sections), [form.sections]);
  const finalized = report?.status === 'finalized';
  const readOnly = finalized;

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError('');
      let query = supabase.from('resale_inspection_reports').select('*');
      if (reportId) query = query.eq('id', reportId);
      else if (booking?.id) query = query.eq('booking_id', booking.id);
      else {
        setLoading(false);
        return;
      }
      const { data, error: queryError } = await query.maybeSingle();
      if (!alive) return;
      if (queryError) {
        setError(queryError.message);
        setLoading(false);
        return;
      }
      setReport(data);
      setForm(formFromReport(data, booking));
      setLoading(false);
    }
    load();
    return () => {
      alive = false;
    };
  }, [reportId, booking?.id]);

  async function persistDraft() {
    if (readOnly) return report;
    setSaving(true);
    setError('');
    const payload = {
      ...reportPayload(form, scores),
      branch_id: branchId || booking?.branch_id,
      booking_id: booking?.id || null,
      vehicle_id: booking?.vehicle_id || report?.vehicle_id || null,
      customer_id: booking?.customer_id || report?.customer_id || null,
      status: 'draft',
    };
    let next;
    if (report?.id) {
      const { data, error: updateError } = await supabase
        .from('resale_inspection_reports')
        .update(payload)
        .eq('id', report.id)
        .select('*')
        .single();
      if (updateError) throw new Error(updateError.message);
      next = data;
    } else if (reportId) {
      throw new Error('Inspection report is still loading.');
    } else if (booking?.id) {
      const { data, error: insertError } = await supabase
        .from('resale_inspection_reports')
        .insert(payload)
        .select('*')
        .single();
      if (insertError) throw new Error(insertError.message);
      next = data;
    } else {
      throw new Error('Missing inspection report.');
    }
    setReport(next);
    onReportChange?.(next);
    setSaving(false);
    return next;
  }

  async function saveDraft({ silent = false } = {}) {
    try {
      await persistDraft();
      if (!silent) setOk('Draft saved.');
    } catch (err) {
      setError(err.message || 'Could not save draft.');
      throw err;
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      saveDraft,
    }),
    [form, scores, report, readOnly, booking, branchId, reportId]
  );

  async function deleteDraft() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    if (!report?.id) return;
    setDeleting(true);
    setError('');
    try {
      const { error: deleteError } = await supabase
        .from('resale_inspection_reports')
        .delete()
        .eq('id', report.id)
        .eq('status', 'draft');
      if (deleteError) throw new Error(deleteError.message);
      onDeleted?.();
    } catch (err) {
      setError(err.message || 'Could not delete draft.');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  async function finalizeReport() {
    if (!confirmFinalize) {
      setConfirmFinalize(true);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const draft = await persistDraft();
      const { data, error: rpcError } = await supabase.rpc(
        'finalize_resale_inspection_report',
        { p_report_id: draft.id }
      );
      if (rpcError) throw new Error(rpcError.message);
      setReport(data);
      setForm(formFromReport(data, booking));
      setOk('Resale inspection finalized.');
      setConfirmFinalize(false);
      onFinalized?.(data);
      onReportChange?.(data);
    } catch (err) {
      setError(err.message || 'Could not finalize report.');
    } finally {
      setSaving(false);
    }
  }

  async function uploadFiles(files, section = 'general') {
    if (!files?.length || readOnly) return;
    setUploading(true);
    setError('');
    try {
      let current = report;
      if (!current?.id) current = await persistDraft();
      const uploaded = [];
      for (const file of files) {
        const ext = file.name.split('.').pop() || 'jpg';
        const path = `resale/${current.id}/${crypto.randomUUID()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('vehicle-photos')
          .upload(path, file, { upsert: false });
        if (uploadError) throw new Error(uploadError.message);
        uploaded.push({
          bucket: 'vehicle-photos',
          path,
          name: file.name,
          kind: kindFromName(file.name) === 'video' ? 'video' : 'photo',
          section,
        });
      }
      setForm((prev) => ({ ...prev, media: [...prev.media, ...uploaded] }));
      setOk('Media uploaded. Save draft to keep changes.');
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  function updateSection(key, patch) {
    setForm((prev) => {
      const current = prev.sections[key] || {};
      let next = { ...current, ...patch };

      if ('status' in patch && !('score' in patch) && !current.scoreManual) {
        next.score = suggestedScoreForStatus(patch.status);
        next.scoreManual = false;
      }

      if ('score' in patch) {
        const value = Number(patch.score);
        next.score = Number.isFinite(value)
          ? Math.max(0, Math.min(100, Math.round(value)))
          : suggestedScoreForStatus(next.status || current.status);
        next.scoreManual = true;
      }

      return {
        ...prev,
        sections: {
          ...prev.sections,
          [key]: next,
        },
      };
    });
  }

  if (loading) return <p className="muted">Loading resale inspection…</p>;

  if (finalized) {
    return (
      <ResaleFinalizedView
        report={report}
        booking={booking}
        onRefresh={(next) => {
          setReport(next);
          onReportChange?.(next);
        }}
      />
    );
  }

  const showSeller = !panelMode || panelMode === 'seller';
  const showInspection = !panelMode || panelMode === 'inspection';
  const showCosts = !panelMode || panelMode === 'costs';
  const showPublish = !panelMode || panelMode === 'publish';
  const showActions = !panelMode || panelMode === 'publish';

  return (
    <div className="resale-panel">
      {error ? <p className="error">{error}</p> : null}
      {ok ? <p className="ok">{ok}</p> : null}

      <div className="resale-score-preview">
        <strong>{scores.overallScore}</strong>
        <span>Overall · {scores.marketCondition.replace(/_/g, ' ')}</span>
      </div>

      {showSeller ? (
        <>
      <section className="resale-block">
        <h3>Seller</h3>
        <div className="resale-grid">
          <label>
            Name
            <input
              value={form.sellerName}
              onChange={(e) => setForm({ ...form, sellerName: e.target.value })}
            />
          </label>
          <label>
            Phone
            <input
              value={form.sellerPhone}
              onChange={(e) => setForm({ ...form, sellerPhone: e.target.value })}
            />
          </label>
          <label>
            City
            <input
              value={form.sellerCity}
              onChange={(e) => setForm({ ...form, sellerCity: e.target.value })}
            />
          </label>
        </div>
      </section>

      <section className="resale-block">
        <h3>Vehicle</h3>
        <div className="resale-grid">
          <label>
            Brand
            <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
          </label>
          <label>
            Model
            <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
          </label>
          <label>
            Year
            <input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
          </label>
          <label>
            Plate
            <input
              value={form.plateNumber}
              onChange={(e) => setForm({ ...form, plateNumber: e.target.value })}
            />
          </label>
          <label>
            VIN
            <input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} />
          </label>
          <label>
            Mileage (km)
            <input
              value={form.mileageKm}
              onChange={(e) => setForm({ ...form, mileageKm: e.target.value })}
            />
          </label>
          <label>
            Color
            <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </label>
          <label>
            Fuel
            <input
              value={form.fuelType}
              onChange={(e) => setForm({ ...form, fuelType: e.target.value })}
            />
          </label>
          <label>
            Transmission
            <input
              value={form.transmission}
              onChange={(e) => setForm({ ...form, transmission: e.target.value })}
            />
          </label>
        </div>
      </section>
        </>
      ) : null}

      {showInspection ? (
      <>
      {RESALE_SECTION_KEYS.map((key) => {
        const section = form.sections[key] || {};
        const suggested = suggestedScoreForStatus(section.status || 'good');
        const displayScore = section.score ?? suggested;
        return (
          <section key={key} className="resale-block">
            <h3>{RESALE_SECTION_LABELS[key]}</h3>
            <div className="resale-section-row">
              <label>
                Status
                <select
                  value={section.status || 'good'}
                  onChange={(e) => updateSection(key, { status: e.target.value })}
                >
                  {RESALE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Score
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={displayScore}
                  onChange={(e) =>
                    updateSection(key, { score: e.target.value })
                  }
                />
                <small className="muted">
                  {section.scoreManual
                    ? 'Manual override'
                    : `Auto from status (${suggested})`}
                </small>
              </label>
            </div>
            <label>
              Notes
              <textarea
                rows={2}
                value={section.notes || ''}
                onChange={(e) => updateSection(key, { notes: e.target.value })}
              />
            </label>
            {key === 'paint_thickness' ? (
              <label>
                Panel readings (one per line: panel, microns)
                <textarea
                  rows={3}
                  value={(section.readings || [])
                    .map((r) => `${r.panel}, ${r.microns}`)
                    .join('\n')}
                  onChange={(e) => {
                    const readings = e.target.value
                      .split('\n')
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line) => {
                        const [panel, microns] = line.split(',').map((p) => p.trim());
                        return { panel, microns: Number(microns) || 0 };
                      });
                    updateSection(key, { readings });
                  }}
                />
              </label>
            ) : null}
            {key === 'computer_diagnosis' ? (
              <label>
                DTC codes (comma separated)
                <input
                  value={(section.dtc_codes || []).join(', ')}
                  onChange={(e) =>
                    updateSection(key, {
                      dtc_codes: e.target.value
                        .split(',')
                        .map((v) => v.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </label>
            ) : null}
            <label className="resale-upload">
              Add media
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                disabled={uploading}
                onChange={(e) => uploadFiles(e.target.files, key)}
              />
            </label>
          </section>
        );
      })}
      </>
      ) : null}

      {showCosts ? (
      <>
      <section className="resale-block">
        <h3>Immediate repairs</h3>
        {form.immediateRepairs.map((row, index) => (
          <div key={row.key} className="resale-line-row">
            <input
              placeholder="Repair item"
              value={row.title}
              onChange={(e) => {
                const next = [...form.immediateRepairs];
                next[index] = { ...row, title: e.target.value };
                setForm({ ...form, immediateRepairs: next });
              }}
            />
            <select
              value={row.priority}
              onChange={(e) => {
                const next = [...form.immediateRepairs];
                next[index] = { ...row, priority: e.target.value };
                setForm({ ...form, immediateRepairs: next });
              }}
            >
              <option value="urgent">Urgent</option>
              <option value="soon">Soon</option>
            </select>
            <input
              placeholder="Min EGP"
              value={row.cost_min_egp}
              onChange={(e) => {
                const next = [...form.immediateRepairs];
                next[index] = { ...row, cost_min_egp: e.target.value };
                setForm({ ...form, immediateRepairs: next });
              }}
            />
            <input
              placeholder="Max EGP"
              value={row.cost_max_egp}
              onChange={(e) => {
                const next = [...form.immediateRepairs];
                next[index] = { ...row, cost_max_egp: e.target.value };
                setForm({ ...form, immediateRepairs: next });
              }}
            />
          </div>
        ))}
        <button
          type="button"
          className="ghost"
          onClick={() =>
            setForm({
              ...form,
              immediateRepairs: [...form.immediateRepairs, emptyRepair()],
            })
          }
        >
          Add repair line
        </button>
      </section>

      <section className="resale-block">
        <h3>Expected maintenance</h3>
        {form.expectedMaintenance.map((row, index) => (
          <div key={row.key} className="resale-line-row">
            <input
              placeholder="Maintenance item"
              value={row.item}
              onChange={(e) => {
                const next = [...form.expectedMaintenance];
                next[index] = { ...row, item: e.target.value };
                setForm({ ...form, expectedMaintenance: next });
              }}
            />
            <input
              placeholder="Months"
              value={row.horizon_months}
              onChange={(e) => {
                const next = [...form.expectedMaintenance];
                next[index] = { ...row, horizon_months: e.target.value };
                setForm({ ...form, expectedMaintenance: next });
              }}
            />
            <input
              placeholder="Cost EGP"
              value={row.cost_egp}
              onChange={(e) => {
                const next = [...form.expectedMaintenance];
                next[index] = { ...row, cost_egp: e.target.value };
                setForm({ ...form, expectedMaintenance: next });
              }}
            />
          </div>
        ))}
        <button
          type="button"
          className="ghost"
          onClick={() =>
            setForm({
              ...form,
              expectedMaintenance: [...form.expectedMaintenance, emptyMaintenance()],
            })
          }
        >
          Add maintenance line
        </button>
      </section>

      <section className="resale-block">
        <h3>Summary</h3>
        <label>
          Inspector name
          <input
            value={form.inspectorName}
            onChange={(e) => setForm({ ...form, inspectorName: e.target.value })}
          />
        </label>
        <label>
          Summary notes
          <textarea
            rows={3}
            value={form.summaryNotes}
            onChange={(e) => setForm({ ...form, summaryNotes: e.target.value })}
          />
        </label>
      </section>

      {form.media.length ? (
        <section className="resale-block">
          <h3>Media ({form.media.length})</h3>
          <div className="resale-media-grid">
            {form.media.map((item) => (
              <ResaleMediaThumb key={item.path} item={item} />
            ))}
          </div>
        </section>
      ) : null}
      </>
      ) : null}

      {showActions ? (
      <div className="resale-actions">
        <button type="button" className="ghost" disabled={saving} onClick={() => saveDraft()}>
          {saving ? 'Saving…' : 'Save draft'}
        </button>
        {!booking ? (
        <button
          type="button"
          className="ghost danger"
          disabled={saving || deleting}
          onClick={deleteDraft}
        >
          {deleting ? 'Deleting…' : confirmDelete ? 'Confirm delete' : 'Delete draft'}
        </button>
        ) : null}
        {confirmDelete ? (
          <button
            type="button"
            className="ghost"
            disabled={deleting}
            onClick={() => setConfirmDelete(false)}
          >
            Cancel
          </button>
        ) : null}
      </div>
      ) : null}

      {showPublish ? (
      <ConfirmBar
        message={
          confirmFinalize
            ? 'Finalize this certified resale inspection? The seller can share it with buyers after publishing.'
            : 'Ready to finalize the resale inspection?'
        }
        confirmLabel={confirmFinalize ? 'Finalize report' : 'Continue'}
        cancelLabel={confirmFinalize ? 'Cancel' : 'Back'}
        busy={saving}
        onConfirm={finalizeReport}
        onCancel={() => setConfirmFinalize(false)}
      />
      ) : null}
    </div>
  );
});
